import { createContext, useContext, useState } from "react";
import { toast } from "sonner";
import { ArrowRight, MapPin, Sparkles } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { postToZohoWebToLead, zohoLeadSource } from "@/lib/zoho";

const SLOTS = ["11:00 AM", "3:30 PM", "6:00 PM"];

const VARIANTS = {
  default: {
    title: "Book an appointment",
    desc: "A free 15-minute call with a Wealth-Maker. No pressure, no pooled-fund pitch.",
    submit: "Request my call",
    showSlots: true,
    slotTag: null,
  },
  ayodhya: {
    title: "Arrange your Ayodhya site visit",
    desc: "Walk the parcels, understand the connectivity, and seek blessings at Ram Mandir. Leave your details and we'll set it up.",
    submit: "Request my site visit",
    showSlots: false,
    slotTag: "Ayodhya site visit",
  },
};

const BookingContext = createContext(null);
export const useBooking = () => useContext(BookingContext);

function BookingForm({ variant, source, onDone }) {
  const cfg = VARIANTS[variant] || VARIANTS.default;
  const [form, setForm] = useState({ name: "", phone: "", email: "", message: "" });
  const [slot, setSlot] = useState(SLOTS[0]);
  const [loading, setLoading] = useState(false);
  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });
  const field = "w-full bg-transparent border-b border-brand-ink/20 py-2.5 text-brand-ink placeholder:text-brand-grey/70 focus:border-brand-orange focus:outline-none transition-colors";

  const submit = (e) => {
    e.preventDefault();
    if (!form.name || !form.phone || !form.email) { toast.error("Please fill in your name, phone and email."); return; }
    setLoading(true);
    const leadSource = source || "modal";
    // Static site: the only destination is Zoho CRM Web-to-Lead
    // (fire-and-forget hidden-iframe POST, cross-origin response unreadable).
    // Success UX is therefore driven by client-side validation passing.
    postToZohoWebToLead({ name: form.name, phone: form.phone, email: form.email, message: form.message, source: leadSource, slot: cfg.showSlots ? slot : "" });
    window.dataLayer = window.dataLayer || [];
    window.dataLayer.push({ event: "lead_form_submit", form_location: zohoLeadSource(leadSource) });
    toast.success("Thank you! A Wealth-Maker will be in touch shortly.");
    setForm({ name: "", phone: "", email: "", message: "" });
    setLoading(false);
    onDone && onDone();
  };

  return (
    <form onSubmit={submit} data-testid="booking-form" className="space-y-5 mt-2">
      {variant === "ayodhya" && (
        <div className="flex flex-wrap gap-4 text-xs text-brand-grey">
          <span className="inline-flex items-center gap-1.5"><MapPin className="w-3.5 h-3.5 text-brand-orange" /> Visit the land</span>
          <span className="inline-flex items-center gap-1.5"><Sparkles className="w-3.5 h-3.5 text-brand-orange" /> VIP Ram Mandir darshan</span>
        </div>
      )}
      <input data-testid="booking-name" className={field} placeholder="Full name" value={form.name} onChange={set("name")} />
      <input data-testid="booking-phone" className={field} placeholder="Phone number" value={form.phone} onChange={set("phone")} />
      <input data-testid="booking-email" type="email" className={field} placeholder="Email address" value={form.email} onChange={set("email")} />
      <textarea data-testid="booking-note" rows={2} className={field + " resize-none"} placeholder="Anything we should know? (optional)" value={form.message} onChange={set("message")} />
      {cfg.showSlots && (
        <div>
          <p className="text-xs tracking-[0.15em] uppercase text-brand-grey mb-3">Preferred slot today</p>
          <div className="flex flex-wrap gap-2">
            {SLOTS.map((s) => (
              <button key={s} type="button" data-testid={`booking-slot-${s.replace(/[:\s]/g, "")}`} onClick={() => setSlot(s)}
                className={`px-4 py-2 rounded-sm border text-sm font-medium transition-all duration-300 ${slot === s ? "bg-brand-orange border-brand-orange text-white" : "border-brand-ink/20 text-brand-grey hover:border-brand-ink/50"}`}>{s}</button>
            ))}
          </div>
        </div>
      )}
      <button type="submit" data-testid="booking-submit" disabled={loading}
        className="group w-full inline-flex items-center justify-center gap-2 rounded-full bg-brand-orange px-8 py-3.5 font-semibold text-white hover:bg-brand-ink transition-colors duration-300 disabled:opacity-60">
        {loading ? "Sending…" : cfg.submit}<ArrowRight className="w-5 h-5 transition-transform group-hover:translate-x-1" />
      </button>
      <p className="text-xs text-brand-grey/70">By submitting you agree to be contacted by BWG. We never share your details.</p>
    </form>
  );
}

export function BookingProvider({ children }) {
  const [state, setState] = useState({ open: false, variant: "default", source: "modal" });
  const open = (variant = "default", source = "modal") => setState({ open: true, variant, source });
  const close = () => setState((s) => ({ ...s, open: false }));
  const cfg = VARIANTS[state.variant] || VARIANTS.default;

  return (
    <BookingContext.Provider value={{ open, close }}>
      {children}
      <Dialog open={state.open} onOpenChange={(o) => !o && close()}>
        <DialogContent data-testid="booking-modal" className="bg-brand-offwhite border-brand-ink/10 sm:rounded-sm max-w-md">
          <DialogHeader>
            <DialogTitle className="font-display text-2xl tracking-tight text-brand-ink">{cfg.title}</DialogTitle>
            <DialogDescription className="text-brand-grey">{cfg.desc}</DialogDescription>
          </DialogHeader>
          <BookingForm variant={state.variant} source={state.source} onDone={close} />
        </DialogContent>
      </Dialog>
    </BookingContext.Provider>
  );
}
